import React, { useState, useEffect, useContext } from 'react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { 
  Layers, FileText, Loader2, Play, CheckCircle, AlertCircle,
  Filter, BarChart, Shield, Calendar, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { useLanguage, translations } from '@/contexts/LanguageContext.jsx';

export const BatchAnalysisPage = () => {
  const token = localStorage.getItem('token');
  const { language } = useLanguage();
  const t = translations[language];
  const [documents, setDocuments] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [analysisType, setAnalysisType] = useState('summary');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [contractsOnly, setContractsOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [batchJob, setBatchJob] = useState(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    loadDocuments();
  }, []);

  useEffect(() => {
    let interval;
    if (polling && batchJob?.status === 'processing') {
      interval = setInterval(checkBatchStatus, 3000);
    }
    return () => clearInterval(interval);
  }, [polling, batchJob]);

  const loadDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    }
  };

  const checkBatchStatus = async () => {
    if (!batchJob?.batch_id) return;
    
    try {
      const response = await axios.get(`${API}/documents/batch-status/${batchJob.batch_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBatchJob(response.data);
      
      if (response.data.status === 'completed') {
        setPolling(false);
        toast.success(t.batchAnalysisComplete);
      }
    } catch (error) {
      console.error('Failed to check status');
    }
  };

  const startAnalysis = async () => {
    setLoading(true);
    setBatchJob(null);

    try {
      const filters = {};
      if (departmentFilter) filters.department = departmentFilter;
      if (contractsOnly) filters.is_contract = true;

      const response = await axios.post(`${API}/documents/batch-analyze`, {
        document_ids: selectedDocs.length > 0 ? selectedDocs : [],
        analysis_type: analysisType,
        filters: Object.keys(filters).length > 0 ? filters : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setBatchJob(response.data);
      setPolling(true);
      toast.success(t.startedAnalyzing?.replace('{count}', response.data.document_count) || `Started analyzing ${response.data.document_count} documents`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t.failedStartAnalysis);
    } finally {
      setLoading(false);
    }
  };

  const toggleDocument = (docId) => {
    setSelectedDocs(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const selectAll = () => {
    if (selectedDocs.length === documents.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(documents.map(d => d.id));
    }
  };

  const analysisTypes = [
    { value: 'summary', label: t.summaryAnalysis, icon: FileText, description: t.briefOverview },
    { value: 'compliance', label: t.complianceAnalysis, icon: Shield, description: t.checkCompliance },
    { value: 'risk', label: t.riskAnalysis, icon: AlertCircle, description: t.identifyRisks },
    { value: 'expiration', label: t.datesDeadlines, icon: Calendar, description: t.extractAllDates }
  ];

  const departments = [...new Set(documents.map(d => d.department).filter(Boolean))];

  return (
    <div className="p-8 bg-slate-950 min-h-full">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white tracking-tight flex items-center gap-3">
          <Layers className="text-indigo-400" />
          {t.batchAnalysis}
        </h1>
        <p className="text-base text-slate-400 mt-2">{t.analyzeMultipleDocs}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Document Selection */}
        <div className="lg:col-span-2">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t.selectDocumentsTitle}</CardTitle>
                <Button variant="ghost" size="sm" onClick={selectAll}>
                  {selectedDocs.length === documents.length ? t.deselectAll : t.selectAll}
                </Button>
              </div>
              <CardDescription>
                {selectedDocs.length} {t.selectedOf} {documents.length} {t.selected}
                {selectedDocs.length === 0 && ` ${t.allDocsAnalyzed}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex gap-4 mb-4">
                <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                  <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder={t.allDepartmentsFilter} />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all">{t.allDepartmentsFilter}</SelectItem>
                    {departments.map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="contractsOnly"
                    checked={contractsOnly}
                    onCheckedChange={setContractsOnly}
                  />
                  <label htmlFor="contractsOnly" className="text-sm text-slate-400">
                    {t.contractsOnlyFilter}
                  </label>
                </div>
              </div>

              {/* Document List */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {documents
                  .filter(d => !departmentFilter || departmentFilter === 'all' || d.department === departmentFilter)
                  .filter(d => !contractsOnly || d.is_contract)
                  .map(doc => (
                    <div
                      key={doc.id}
                      onClick={() => toggleDocument(doc.id)}
                      className={`p-3 rounded-lg cursor-pointer transition-all flex items-center gap-3 ${
                        selectedDocs.includes(doc.id)
                          ? 'bg-indigo-500/20 border border-indigo-500/50'
                          : 'bg-slate-800/50 hover:bg-slate-800 border border-transparent'
                      }`}
                    >
                      <Checkbox checked={selectedDocs.includes(doc.id)} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{doc.title}</div>
                        <div className="text-xs text-slate-500 flex gap-2 mt-1">
                          {doc.department && <span>{doc.department}</span>}
                          {doc.is_contract && <span className="text-yellow-500">Contract</span>}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right: Analysis Options & Results */}
        <div className="space-y-6">
          {/* Analysis Type */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg">{t.analysisTypeTitle}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {analysisTypes.map(type => (
                  <button
                    key={type.value}
                    onClick={() => setAnalysisType(type.value)}
                    className={`w-full p-3 rounded-lg border text-left transition-all flex items-center gap-3 ${
                      analysisType === type.value
                        ? 'border-indigo-500 bg-indigo-500/10'
                        : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                    }`}
                  >
                    <type.icon className={`w-5 h-5 ${analysisType === type.value ? 'text-indigo-400' : 'text-slate-400'}`} />
                    <div>
                      <div className="text-sm font-medium text-white">{type.label}</div>
                      <div className="text-xs text-slate-500">{type.description}</div>
                    </div>
                  </button>
                ))}
              </div>

              <Button
                onClick={startAnalysis}
                disabled={loading || polling}
                className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700"
                data-testid="start-batch-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t.starting}
                  </>
                ) : polling ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {t.processing}
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    {t.startAnalysisBtn}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Progress */}
          {batchJob && (
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  {batchJob.status === 'completed' ? (
                    <CheckCircle className="text-green-400" size={18} />
                  ) : (
                    <Loader2 className="animate-spin text-indigo-400" size={18} />
                  )}
                  {batchJob.status === 'completed' ? t.analysisCompleteTitle : t.processingTitle}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Progress value={batchJob.progress || 0} className="mb-2" />
                <p className="text-sm text-slate-400">
                  {batchJob.progress}% {t.percentComplete} • {batchJob.document_count} {t.documents?.toLowerCase()}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Results */}
      {batchJob?.results?.length > 0 && (
        <Card className="bg-slate-900/50 border-slate-800 mt-6">
          <CardHeader>
            <CardTitle>{t.analysisResultsTitle}</CardTitle>
            <CardDescription>
              {batchJob.results.filter(r => r.status === 'completed').length} {t.completed},{' '}
              {batchJob.results.filter(r => r.status === 'error').length} {t.errors}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {batchJob.results.map((result, index) => (
                <div key={index} className="p-4 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-white font-medium">{result.title}</h4>
                    {result.status === 'completed' ? (
                      <CheckCircle className="text-green-400" size={16} />
                    ) : result.status === 'error' ? (
                      <AlertCircle className="text-red-400" size={16} />
                    ) : (
                      <span className="text-yellow-400 text-xs">{t.skipped}</span>
                    )}
                  </div>
                  <div className="prose prose-invert prose-sm max-w-none">
                    <ReactMarkdown>{result.analysis}</ReactMarkdown>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BatchAnalysisPage;
